import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, CheckCircle, AlertCircle, Link2, Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api from '../utils/api';

/**
 * Google OAuth Callback Handler
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
const GoogleAuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser } = useAuth();
  const [status, setStatus] = useState('processing'); // processing, success, error, link_account
  const [errorMessage, setErrorMessage] = useState('');
  const [linkingData, setLinkingData] = useState(null);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    const processGoogleCallback = async () => {
      const code = searchParams.get('code');
      const error = searchParams.get('error');

      if (error) {
        setStatus('error');
        setErrorMessage('Google authentication was cancelled or failed.');
        return;
      }

      if (!code) {
        setStatus('error');
        setErrorMessage('Invalid authentication response. No authorization code received.');
        return;
      }

      try {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUri = window.location.origin + '/auth/google';
        
        // Send authorization code to backend
        const response = await api.post('/auth/google', {
          code: code,
          redirect_uri: redirectUri
        });

        const data = response.data;

        // Check if account linking is required
        if (data.requires_linking) {
          setLinkingData({
            email: data.email,
            google_id: data.google_id,
            google_name: data.google_name,
            google_picture: data.google_picture
          });
          setStatus('link_account');
          return;
        }

        const { access_token, user } = data;

        // Store token
        localStorage.setItem('token', access_token);
        
        // Update auth context
        setUser(user);

        setStatus('success');
        toast.success(`Welcome, ${user.first_name || user.email}!`);

        // Redirect based on role
        setTimeout(() => {
          if (user.role === 'admin' || user.role === 'sub_admin' || user.role === 'super_admin') {
            navigate('/admin/dashboard');
          } else {
            navigate('/client/dashboard');
          }
        }, 1500);

      } catch (err) {
        console.error('Google OAuth callback error:', err);
        setStatus('error');
        setErrorMessage(err.response?.data?.detail || 'Failed to complete Google authentication.');
        toast.error('Authentication failed');
      }
    };

    processGoogleCallback();
  }, [searchParams, navigate, setUser]);

  const handleLinkAccount = async (e) => {
    e.preventDefault();
    
    if (!password) {
      toast.error('Please enter your password');
      return;
    }

    setLinking(true);

    try {
      const response = await api.post('/auth/google/link', {
        email: linkingData.email,
        password: password,
        google_id: linkingData.google_id,
        google_name: linkingData.google_name,
        google_picture: linkingData.google_picture
      });

      const { access_token, user } = response.data;

      // Store token
      localStorage.setItem('token', access_token);
      
      // Update auth context
      setUser(user);

      setStatus('success');
      toast.success('Google account linked successfully!');

      // Redirect based on role
      setTimeout(() => {
        if (user.role === 'admin' || user.role === 'sub_admin' || user.role === 'super_admin') {
          navigate('/admin/dashboard');
        } else {
          navigate('/client/dashboard');
        }
      }, 1500);

    } catch (err) {
      console.error('Account linking error:', err);
      toast.error(err.response?.data?.detail || 'Failed to link account. Please check your password.');
    } finally {
      setLinking(false);
    }
  };

  const handleSkipLinking = () => {
    navigate('/login');
    toast.info('You can sign in with your email and password instead.');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="glass rounded-2xl p-8 text-center animate-fade-in">
          {status === 'processing' && (
            <>
              <Loader2 className="w-16 h-16 text-teal-600 animate-spin mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Signing you in...</h2>
              <p className="text-gray-600">Please wait while we complete your Google authentication.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome!</h2>
              <p className="text-gray-600">Redirecting to your dashboard...</p>
            </>
          )}

          {status === 'link_account' && (
            <>
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Link2 className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Link Your Account</h2>
              <p className="text-gray-600 mb-6">
                An account with <span className="font-medium">{linkingData?.email}</span> already exists. 
                Enter your password to link your Google account.
              </p>
              
              <form onSubmit={handleLinkAccount} className="text-left space-y-4">
                <div>
                  <Label htmlFor="password">Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      className="pl-10 pr-10"
                      data-testid="link-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white font-medium"
                  disabled={linking}
                  data-testid="link-account-button"
                >
                  {linking ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Linking Account...
                    </>
                  ) : (
                    <>
                      <Link2 className="w-5 h-5 mr-2" />
                      Link Google Account
                    </>
                  )}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleSkipLinking}
                  data-testid="skip-linking-button"
                >
                  Cancel and Sign in with Password
                </Button>
              </form>

              <p className="text-xs text-gray-500 mt-4">
                After linking, you can sign in with either your password or Google.
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Authentication Failed</h2>
              <p className="text-gray-600 mb-6">{errorMessage}</p>
              <button
                onClick={() => navigate('/login')}
                className="w-full h-12 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white font-medium rounded-lg"
              >
                Back to Login
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoogleAuthCallback;
